import React, { useEffect, useMemo, useState } from 'react';
import { StripeAddresssField } from '@components/stripe/stripe-address-field';
import { useApp } from '@context/AppContext';
import { useOrganization } from '@context/OrganizationContext';
import {
  useSubscriptions,
  useSubscriptionsDispatch,
} from '@features/subscriptions/context/subscription-context';
import { useCreateSubscription } from '@features/subscriptions/hooks/useCreateSubscription';
import { useCreateSubscriptionCustomer } from '@features/subscriptions/hooks/useCreateSubscriptionCustomer';
import { useGetPaymentMethods } from '@features/subscriptions/hooks/useGetPaymentMethods';
import { useUpdateSubscription } from '@features/subscriptions/hooks/useUpdateSubscription';
import { Button, FlexView, Loading, Text } from '@goodfynd/ui';
import { renderPrice } from '@utils/number-util';

import { SubscriptionCheckoutPaymentMethods } from './SubscriptionCheckoutPaymentMethods';
import { SuiteCheckoutItemList } from './SuiteCheckoutItemList';

import classes from './SubscriptionsCheckout.module.css';

export function SuiteCheckout() {
  const { user } = useApp();
  const { organization, organizationId } = useOrganization();
  const { activeSuite, cart, newSuite, suites } = useSubscriptions();
  const { changeSuite, resetSubscriptions, setPaymentMethod } =
    useSubscriptionsDispatch();
  const frequencyPrice = useMemo(
    () =>
      suites
        .find((suite) => suite.sku === newSuite?.sku)
        ?.prices.find((price) => price.frequency === cart.frequency)?.price ??
      0,
    [cart.frequency, newSuite?.sku, suites]
  );

  const suiteProduct = useMemo(
    () => suites.find((suite) => suite.sku === newSuite?.sku),
    [newSuite?.sku, suites]
  );

  const [customerId, setCustomerId] = useState<string | undefined>(
    organization?.stripeSaasCustomerId
  );
  const { createSubscriptionCustomer, isCreatingSubscriptionCustomer } =
    useCreateSubscriptionCustomer();
  useEffect(() => {
    if (organization && !organization.stripeSaasCustomerId) {
      createSubscriptionCustomer(
        {
          email: user?.email,
        },
        {
          onSuccess(data) {
            setCustomerId(data.id);
          },
        }
      );
    }
  }, [createSubscriptionCustomer, organization, user?.email]);

  const {
    defaultPaymentMethod,
    paymentMethods = [],
    isFetching: isFetchingPaymentMethods,
  } = useGetPaymentMethods(organizationId, {
    enabled: !!customerId,
    onSuccess(data) {
      if (!data[0]) {
        return;
      }

      const defaultPaymentMethod =
        data.find((paymentMethod) => paymentMethod.isDefault) ?? data[0];
      setPaymentMethod(defaultPaymentMethod.id);
    },
  });

  const { createSubscription, isCreatingSubscription } = useCreateSubscription(
    organizationId,
    {
      onSuccess() {
        changeSuite(undefined);
        resetSubscriptions();
      },
    }
  );
  const { updateSubscription, isUpdatingSubscription } = useUpdateSubscription(
    organizationId,
    activeSuite?.subscriptionId,
    {
      onSuccess() {
        changeSuite(undefined);
        resetSubscriptions();
      },
    }
  );

  if (isCreatingSubscriptionCustomer || isFetchingPaymentMethods) {
    return <Loading />;
  }

  return (
    <FlexView
      className={classes.container}
      direction="column"
      gap={32}
      width="100%"
      wrap="nowrap"
    >
      <SuiteCheckoutItemList />

      <FlexView gap={16} justify="space-between" width="100%">
        <Text as="sectionHeader">Subtotal</Text>
        <Text as="sectionHeader">{renderPrice(frequencyPrice)}</Text>
      </FlexView>

      {!organization?.location && (
        <StripeAddresssField
          onChange={(address) => console.log('Address changed:', address)}
        />
      )}

      {frequencyPrice > 0 && (
        <SubscriptionCheckoutPaymentMethods
          defaultPaymentMethod={defaultPaymentMethod}
          savedPaymentMethods={paymentMethods}
        />
      )}
      <Button
        busy={isCreatingSubscription || isUpdatingSubscription}
        content="Place order"
        disabled={!cart.paymentMethodId && frequencyPrice > 0}
        fullWidth
        onClick={async () => {
          if (!newSuite || !suiteProduct) {
            return;
          }

          if (activeSuite) {
            return await updateSubscription(
              suiteProduct,
              newSuite?.priceId,
              cart.paymentMethodId
            );
          }

          return await createSubscription(
            suiteProduct,
            newSuite?.priceId,
            cart.paymentMethodId
          );
        }}
      />
    </FlexView>
  );
}
